import React, { memo, useCallback, useEffect, useState } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { ZodObject } from "zod";
import { useFormStore } from "./state";
import { Card } from "../ui/card";
import ProgressBar from "../institution/ProgressBar";
import { ELEMENT_TYPES } from "@ept/shared";
import { findValidatorWithType, isElementNotEmpty } from "@ept/shared/src/validators";
import { ElementData, EptForm } from "../form-builder/utils/types";
import { SectionStatus } from "./types";
import { getVisibleDynamicElements } from "./utils/element-validators";
import _ from "lodash";
import { useFormProgressBar } from "@/app/uat/[institutionSlug]/components/FormProgressBarContext";
type FormProgressBarProps = {
  formSchema: ZodObject<any>;
  formData: EptForm | undefined;
  hidden?: boolean;
};
const FormProgressBar: React.FC<FormProgressBarProps> = ({
  formSchema,
  formData,
  hidden = false
}) => {
  const {
    getValues,
    formState: {
      errors
    }
  } = useFormContext();
  const [completionPercentage, setCompletionPercentage] = useState<number>(0);
  const sectionStatuses = useFormStore(state => state.sectionStatuses);
  const values = useWatch();
  const {
    dispatch: progressBarDispatch
  } = useFormProgressBar();
  const columnHasRequiredValidator = useCallback((columnId: string, extraAttributes: ElementData["element"]["extraAttributes"]) => {
    return extraAttributes?.validators?.some(validator => validator.columnName === columnId && validator.type === "REQUIRED");
  }, []);
  const getTableFields = useCallback(() => {
    if (!formData) return [];
    return formData.sections.flatMap(section => section.section.elements.filter(element => element.element.type === "TABLE").flatMap(element => {
      const formInput = getValues()[element.id!];
      return formInput?.flatMap((row: object, index: number) => Object.keys(row).filter(columnId => columnHasRequiredValidator(columnId, element.element.extraAttributes)).map(columnId => `${element.id}.${index}.${columnId}`)) ?? [];
    }));
  }, [formData, getValues, columnHasRequiredValidator]);
  useEffect(() => {
    if (!formData) return;
    const dynamicElements = getVisibleDynamicElements(values, formData) ?? [];
    const requiredFields = [...Object.keys(formSchema.shape).filter(fieldName => !formSchema.shape[fieldName].isOptional?.()), ...dynamicElements.filter(element => findValidatorWithType(element.element.validators, "REQUIRED")).map(element => element.id!), ...getTableFields()];
    const filledRequiredFields = requiredFields.filter(field => {
      const value = _.get(values, field);
      const isValid = value !== undefined && value !== null && !_.get(errors, field);
      if (!isValid) return false;
      const elementId = field.split(".")[0];
      const element = formData.sections.flatMap(section => section.section.elements).find(element => element.id === elementId);
      const type = element?.element.type === "TABLE" ? _.get(element, `element.extraAttributes.columns.find(column => column.id === "${field.split(".")[2]}").type`, "TEXT_BOX") : element?.element.type;
      return isElementNotEmpty(value, type as (typeof ELEMENT_TYPES)[number]);
    });
    const newCompletionPercentage = requiredFields.length ? filledRequiredFields.length / requiredFields.length * 100 : 0;
    setCompletionPercentage(Math.round(newCompletionPercentage));
    if (progressBarDispatch) {
      progressBarDispatch({
        type: "SET_PROGRESS",
        payload: Math.round(newCompletionPercentage)
      });
    }
    const newSectionStatuses = formData.sections.reduce<Record<string, SectionStatus>>((acc, section) => {
      const sectionFields = section.section.elements.filter(element => dynamicElements.some(dynamicElement => dynamicElement.id === element.id) || !element.element.validators.find(validator => validator.validator.type === "CLIENT_SIDE_DYNAMIC")).filter(element => element.element.type !== "TEXT");
      const isSectionCompleted = sectionFields.length > 0 && sectionFields.every(element => !!findValidatorWithType(element.element.validators, "REQUIRED") ? filledRequiredFields.includes(element.id) : true);
      if (section.sectionId) {
        const hasSectionErrors = sectionFields.some(element => element.id !== undefined && Object.prototype.hasOwnProperty.call(errors, element.id));
        acc[section.sectionId] = hasSectionErrors ? "has-errors" : isSectionCompleted ? "completed" : "incomplete";
      }
      return acc;
    }, {});
    if (!_.isEqual(sectionStatuses, newSectionStatuses)) {
      useFormStore.setState({
        sectionStatuses: newSectionStatuses
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formSchema.shape, formData, getTableFields, values, JSON.stringify(errors), sectionStatuses]);
  if (hidden) return null;
  return completionPercentage !== null && <div className="sticky top-3 z-[9999] w-full">
        <div className="relative">
          <div className="absolute left-0 top-0 -mx-8 -mt-4 h-12" style={{
        width: "calc(100% + 3rem)",
        borderRadius: "0 0 2rem 2rem",
        background: "linear-gradient(to top, rgba(255, 255, 255, 0) 0%, hsl(var(--progressbar-background)) 25%)"
      }}>
            <div className="relative h-full w-full"></div>
          </div>
        </div>
        <Card className="mb-1 overflow-hidden rounded-3xl border shadow-md">
          <ProgressBar percentage={completionPercentage} />
        </Card>
      </div>;
};
export default memo(FormProgressBar);